import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. When omitted, a full-screen recovery UI is shown. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * App-level error boundary. Catches render/lifecycle errors from the React tree
 * so a single failing component can't blank the whole app. Shows an honest
 * recovery screen with a reload action instead of a white screen.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for diagnostics; a real deployment can wire this
    // to an error-reporting endpoint here.
    console.error('Uncaught application error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-100 p-6">
        <div className="max-w-md rounded-lg bg-white p-6 text-center shadow-card">
          <h1 className="mb-2 text-lg font-semibold text-gray-900">
            Something went wrong
          </h1>
          <p className="mb-5 text-sm text-gray-600">
            The map ran into an unexpected error. Reloading usually fixes it. If the
            problem keeps happening, please try again later.
          </p>
          <button
            onClick={this.handleReload}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
